import { useEffect, useState } from "react";
import { Smartphone, Share, PlusSquare, X, Download } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

let deferred = null;
const subs = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; subs.forEach((f) => f(e)); });
  window.addEventListener("appinstalled", () => { deferred = null; subs.forEach((f) => f(null)); });
}

export const platform = () => {
  const ua = navigator.userAgent || "";
  const ios = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return { ios, android: /android/i.test(ua), safari: ios && !/crios|fxios|edgios/i.test(ua) };
};
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isPhone = () => { const p = platform(); return p.ios || p.android; };
export const pushLocked = () => isPhone() && !isStandalone();

function useDeferred() {
  const [ev, setEv] = useState(deferred);
  useEffect(() => { subs.add(setEv); return () => { subs.delete(setEv); }; }, []);
  return ev;
}

function Steps() {
  const p = platform();
  if (p.ios) return (
    <ol className="space-y-3 text-sm text-slate-300" data-testid="install-steps-ios">
      {!p.safari && <li className="rounded-lg bg-amber-400/10 p-2 text-xs text-amber-200">Sur iPhone, ouvrez d'abord mapetiteligue.com dans Safari.</li>}
      <li className="flex gap-2"><b className="text-[#B7F52A]">1.</b> Ouvrez mapetiteligue.com dans Safari.</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">2.</b> Touchez <Share size={15} className="inline text-white" /> Partager (ou le menu « … »).</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">3.</b> Choisissez <PlusSquare size={15} className="inline text-white" /> « Sur l'écran d'accueil ».</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">4.</b> Touchez « Ajouter » : l'icône Ma Petite Ligue apparaît.</li>
    </ol>
  );
  return (
    <ol className="space-y-3 text-sm text-slate-300" data-testid="install-steps-android">
      <li className="flex gap-2"><b className="text-[#B7F52A]">1.</b> Ouvrez mapetiteligue.com dans Chrome.</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">2.</b> Touchez le menu ⋮ en haut à droite.</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">3.</b> Choisissez « Installer l'application » ou « Ajouter à l'écran d'accueil ».</li>
      <li className="flex gap-2"><b className="text-[#B7F52A]">4.</b> Confirmez : l'application s'ouvre ensuite en plein écran.</li>
    </ol>
  );
}

export function InstallDialog({ open, onOpenChange }) {
  const ev = useDeferred();
  const native = async () => { await ev.prompt(); await ev.userChoice.catch(() => {}); deferred = null; subs.forEach((f) => f(null)); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0B1020] text-white sm:max-w-md" data-testid="install-dialog">
        <DialogTitle className="flex items-center gap-2 text-lg font-bold"><Smartphone size={18} className="text-[#B7F52A]" /> Installer Ma Petite Ligue</DialogTitle>
        <DialogDescription className="text-xs text-slate-400">Ajoute l'icône sur ton écran d'accueil : l'application s'ouvre en plein écran et peut t'envoyer des notifications.</DialogDescription>
        {isStandalone() ? <p className="text-sm text-[#B7F52A]" data-testid="install-already">Ma Petite Ligue est déjà installée sur cet appareil.</p>
          : ev ? <button onClick={native} className="mpl-btn-primary" data-testid="install-native-button"><Download size={15} /> Installer maintenant</button> : <Steps />}
      </DialogContent>
    </Dialog>
  );
}

export function InstallCard() {
  const ev = useDeferred();
  const [open, setOpen] = useState(false);
  const installed = isStandalone();
  const click = async () => {
    if (ev) { await ev.prompt(); await ev.userChoice.catch(() => {}); deferred = null; subs.forEach((f) => f(null)); } else setOpen(true);
  };
  return (
    <section className="mpl-card flex flex-wrap items-center gap-3 p-4" data-testid="profile-install">
      <Smartphone size={22} className="shrink-0 text-[#B7F52A]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">Ajouter l'application à mon téléphone</p>
        <p className="text-xs text-slate-400">{installed ? "Installée sur cet appareil. Pense à activer les notifications dans Paramètres."
          : platform().ios ? "Sur iPhone, Apple demande quelques gestes : on te les montre." : "Une icône sur ton écran d'accueil, en un clic."}</p>
      </div>
      {installed ? <span className="text-xs font-bold text-[#B7F52A]" data-testid="profile-install-done">Installée</span>
        : <button onClick={click} className="mpl-btn-primary" data-testid="profile-install-button"><Download size={15} /> {ev ? "Installer" : "Ajouter"}</button>}
      <InstallDialog open={open} onOpenChange={setOpen} />
    </section>
  );
}

export function InstallNudge() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let n = Number(localStorage.getItem("mpl_visits") || 0);
    if (!sessionStorage.getItem("mpl_visit_counted")) {
      n += 1;
      sessionStorage.setItem("mpl_visit_counted", "1");
      localStorage.setItem("mpl_visits", String(n));
    }
    const p = platform();
    setShow(n >= 3 && (p.ios || p.android) && !isStandalone() && !localStorage.getItem("mpl_install_dismissed"));
  }, []);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem("mpl_install_dismissed", "1"); setShow(false); };
  return (
    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-[#B7F52A]/20 bg-[#B7F52A]/[0.06] p-3 text-xs" data-testid="install-nudge">
      <Smartphone size={18} className="shrink-0 text-[#B7F52A]" />
      <p className="flex-1 text-slate-200">Ajoutez Ma Petite Ligue à votre écran d'accueil.</p>
      <button onClick={() => setOpen(true)} className="font-bold text-[#B7F52A]" data-testid="install-nudge-open">Comment ?</button>
      <button onClick={dismiss} aria-label="Ne plus afficher" className="text-slate-400 hover:text-white" data-testid="install-nudge-dismiss"><X size={15} /></button>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
